import type { LucideIcon } from 'lucide-react';
import { Button } from './Button';

interface EmptyStateProps {
  icon: LucideIcon;
  /** Source Serif line. States the situation plainly — never "Oops" or an apology. */
  headline: string;
  explanation: string;
  /**
   * Included **only if the viewer has permission for it** (spec §6). A parent
   * never sees "Add an event", so the caller decides rather than the component
   * rendering an action the viewer cannot take.
   */
  action?: { label: string; onClick?: () => void };
}

export function EmptyState({ icon: Icon, headline, explanation, action }: EmptyStateProps) {
  return (
    <div
      className="flex flex-col items-center text-center"
      style={{
        border: '1px dashed var(--color-border-strong)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-7) var(--space-5)',
        gap: 'var(--space-3)',
      }}
    >
      <Icon size={26} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />

      <p
        style={{
          font: 'var(--text-subheading)',
          color: 'var(--color-text-primary)',
          margin: 0,
        }}
      >
        {headline}
      </p>

      <p
        className="t-body"
        style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '34ch' }}
      >
        {explanation}
      </p>

      {action && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
}
